import type { Flow } from '@walkeros/core';
import { parseComponentRef } from '../../core/parse-component-ref.js';

const STEP_KINDS = [
  'source',
  'destination',
  'transformer',
  'collector',
] as const;

/**
 * Overrides structure for destination config properties.
 * Shape mirrors Collector.InitConfig.destinations but without requiring `code`.
 * Used with deepMerge at runtime to overlay mock/disabled flags.
 */
export interface PushOverrides {
  destinations?: Record<
    string,
    {
      config?: {
        mock?: unknown;
        disabled?: boolean;
      };
      simulate?: boolean;
      env?: Record<string, unknown>;
      simulation?: string[];
    }
  >;
  sources?: Record<
    string,
    {
      simulate?: boolean;
    }
  >;
  /** Path-specific transformer mocks: chainPath → { transformerId → mockValue } */
  transformerMocks?: Record<string, Record<string, unknown>>;
  transformers?: Record<
    string,
    {
      simulate?: boolean;
    }
  >;
}

/**
 * Build collector overrides from --simulate and --mock CLI flags.
 *
 * - `--simulate destination.NAME` sets simulate: true on NAME
 * - `--simulate source.NAME` marks source for simulation
 * - `--mock destination.NAME=VALUE` sets mock: JSON-parsed VALUE on NAME
 * - Any destination NOT targeted by simulate or mock gets disabled: true
 * - Returns empty object if no flags are provided
 *
 * @throws if same destination appears in both simulate and mock
 * @throws if step format is invalid (missing `source.` or `destination.` prefix)
 * @throws if --mock is used with a source step
 */
export function buildOverrides(
  flags: { simulate?: string[]; mock?: string[] },
  flowConfig: Flow,
): PushOverrides {
  const simulateFlags = flags.simulate ?? [];
  const mockFlags = flags.mock ?? [];

  // No flags → no overrides
  if (simulateFlags.length === 0 && mockFlags.length === 0) {
    return {};
  }

  const simulateNames = new Set<string>();
  const sourceSimulateNames = new Set<string>();
  const mockNames = new Set<string>();
  const overrides: PushOverrides = {};

  // Parse --simulate flags
  for (const step of simulateFlags) {
    const { type, name } = parseStep(step);
    if (type === 'collector') {
      // Collector enrichment carries no per-step overrides.
      continue;
    } else if (type === 'destination') {
      simulateNames.add(name);
      if (!overrides.destinations) overrides.destinations = {};
      overrides.destinations[name] = { simulate: true };
    } else if (type === 'transformer') {
      if (!overrides.transformers) overrides.transformers = {};
      overrides.transformers[name] = { simulate: true };
    } else {
      sourceSimulateNames.add(name);
      if (!overrides.sources) overrides.sources = {};
      overrides.sources[name] = { simulate: true };
    }
  }

  // Validate: only one step type can be simulated at a time
  const hasSource = sourceSimulateNames.size > 0;
  const hasTransformer =
    overrides.transformers && Object.keys(overrides.transformers).length > 0;
  const hasDestination = simulateNames.size > 0;
  const simulatedTypes = [
    hasSource && 'source',
    hasTransformer && 'transformer',
    hasDestination && 'destination',
  ].filter(Boolean);

  if (simulatedTypes.length > 1) {
    throw new Error(
      `Cannot simulate both ${simulatedTypes.join(' and ')} in the same invocation. ` +
        'Run separate commands for each step type.',
    );
  }

  // Parse --mock flags
  for (const step of mockFlags) {
    const eqIndex = step.indexOf('=');
    if (eqIndex === -1) {
      throw new Error(
        `Invalid --mock format: "${step}". Expected destination.NAME=VALUE`,
      );
    }

    const stepPart = step.slice(0, eqIndex);
    const valuePart = step.slice(eqIndex + 1);
    const parsed = parseStep(stepPart);

    if (parsed.type === 'source') {
      throw new Error(
        `--mock is not supported for sources. Use --simulate source.${parsed.name}`,
      );
    }

    if (parsed.type === 'transformer' && !parsed.chainType) {
      throw new Error(
        `Use --mock destination.NAME.before.${parsed.name}=VALUE for path-specific transformer mocks`,
      );
    }

    let parsedValue: unknown;
    try {
      parsedValue = JSON.parse(valuePart);
    } catch {
      // If not valid JSON, use as raw string
      parsedValue = valuePart;
    }

    if (parsed.chainType && parsed.transformerId) {
      // Path-specific mock: destination.ga4.before.redact
      const chainPath = `destination.${parsed.name}.${parsed.chainType}`;
      if (!overrides.transformerMocks) overrides.transformerMocks = {};
      if (!overrides.transformerMocks[chainPath])
        overrides.transformerMocks[chainPath] = {};
      overrides.transformerMocks[chainPath][parsed.transformerId] = parsedValue;
    } else {
      // Simple destination mock (existing behavior)
      // Validate: same destination cannot be in both simulate and mock
      if (simulateNames.has(parsed.name)) {
        throw new Error(
          `Destination "${parsed.name}" cannot be in both --simulate and --mock`,
        );
      }

      mockNames.add(parsed.name);

      if (!overrides.destinations) overrides.destinations = {};
      overrides.destinations[parsed.name] = { config: { mock: parsedValue } };
    }
  }

  // Simulate-implies-disabled: disable all other destinations
  const allDestinations = Object.keys(flowConfig.destinations ?? {});
  const targetedNames = new Set([...simulateNames, ...mockNames]);

  if (targetedNames.size > 0) {
    if (!overrides.destinations) overrides.destinations = {};
    for (const destName of allDestinations) {
      if (!targetedNames.has(destName)) {
        overrides.destinations[destName] = { config: { disabled: true } };
      }
    }
  }

  return overrides;
}

export interface ParsedStep {
  type: 'source' | 'destination' | 'transformer' | 'collector';
  name: string;
  chainType?: 'before' | 'next';
  transformerId?: string;
}

/**
 * Parse a step string in `source.NAME` or `destination.NAME` format.
 * Also supports 4-part path notation: `destination.NAME.CHAIN.TRANSFORMER`
 * @throws if format is invalid
 */
export function parseStep(step: string): ParsedStep {
  // Shared `<prefix>.<name>` parsing lives in core/parse-component-ref.
  // We layer the chain-aware logic (destination.NAME.before.TRANSFORMER) on top.
  const { prefix, name, rest } = parseComponentRef(step, {
    allowed: STEP_KINDS,
    messages: {
      invalidFormat: (input) =>
        `Invalid step format: "${input}". Expected "source.NAME" or "destination.NAME"`,
      invalidPrefix: (p) =>
        `Unsupported step type: "${p}". Use "source", "destination", or "transformer"`,
      missingName: (input, p) =>
        `Invalid step format: "${input}". Missing name after "${p}."`,
    },
  });

  // Path-specific: destination.ga4.before.redact
  if (rest.length >= 2) {
    const chainType = rest[0];
    if (chainType !== 'before' && chainType !== 'next') {
      throw new Error(
        `Invalid chain type: "${chainType}". Use "before" or "next"`,
      );
    }
    const transformerId = rest[1];
    if (!transformerId) {
      throw new Error(
        `Invalid step format: "${step}". Missing transformer name after "${chainType}."`,
      );
    }
    return { type: prefix, name, chainType, transformerId };
  }

  // 3-part (destination.ga4.before without transformer) — invalid
  if (rest.length === 1) {
    throw new Error(
      `Invalid step format: "${step}". Specify a transformer: "${step}.TRANSFORMER_NAME"`,
    );
  }

  return { type: prefix, name };
}
